import { Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { ReportNodeImageBox } from "components/ReportNodeImage/ReportNodeImageBox"
import { ReportNodeImageEditDialog } from "components/ReportNodeImage/ReportNodeImageEditDialog"
import { ReportNodeTableBox } from "components/ReportNodeTable/ReportNodeTableBox"
import { ReportNodeTableEditDialog } from "components/ReportNodeTable/ReportNodeTableEditDialog"
import { ReportNodeTextBox } from "components/ReportNodeText/ReportNodeTextBox"
import { ReportNodeTextEditDialog } from "components/ReportNodeText/ReportNodeTextEditDialog"
import { Criteria, DataNode, NodeDataType } from "graphql/types"
import { useEffect, useState } from "react"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"
import { v4 } from "uuid"

type CriteriaEditDialogProps = {
  open: boolean
  onClose: () => void
  criteria: Criteria | null
  onSave: (criteria: Criteria) => void
}

export const CriteriaEditDialog = (props: CriteriaEditDialogProps) => {
  const { open, onClose, criteria, onSave } = props

  const [localCriteria, setLocalCriteria] = useState<Criteria>(
    criteria || {
      id: "",
      name: "",
      description: [],
      operatingModes: [],
    }
  )
  const [criteriaNode, setCriteriaNode] = useState<DataNode>()

  // Update local state when criteria prop changes
  useEffect(() => {
    if (criteria) {
      setLocalCriteria(criteria)
    } else {
      setLocalCriteria({
        id: "",
        name: "",
        description: [],
        operatingModes: [],
      })
    }
  }, [criteria])

  // Criteria node handlers
  const handleOpenAddCriteriaNode = (type: NodeDataType) => {
    const newNode: DataNode = {
      id: v4(),
      dataType: type,
      editable: true,
      deletable: true,
      image: [],
      imageCaption: [],
      imageNumber: [],
      tableBody: [],
      tableCaption: "",
      tableHeader: [],
      tableNumber: 1,
      text: "",
    }
    setCriteriaNode(newNode)
  }

  const handleCloseAddCriteriaNode = () => {
    setCriteriaNode(undefined)
  }

  const handleAddCriteriaNode = () => {
    if (criteriaNode) {
      const isOldNode = localCriteria.description.find((item) => item.id === criteriaNode?.id)
      if (isOldNode) {
        setLocalCriteria((prev) => ({
          ...prev,
          description: prev.description.map((item) =>
            item.id === criteriaNode?.id ? criteriaNode : item
          ),
        }))
      } else {
        setLocalCriteria((prev) => ({
          ...prev,
          description: [...prev.description, criteriaNode],
        }))
      }
      handleCloseAddCriteriaNode()
    }
  }

  const handleDeleteCriteriaNode = (id: string) => {
    setLocalCriteria((prev) => ({
      ...prev,
      description: prev.description.filter((item) => item.id !== id),
    }))
  }

  const handleEditCriteriaNode = (node: DataNode) => {
    setCriteriaNode(node)
  }

  const handleSave = () => {
    if (localCriteria.name?.trim()) {
      onSave(localCriteria)
    }
  }

  const handleClose = () => {
    // Reset all state when closing
    setLocalCriteria({
      id: "",
      name: "",
      description: [],
      operatingModes: [],
    })
    setCriteriaNode(undefined)
    onClose()
  }

  const isEditing = criteria !== null

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>{isEditing ? "Kriterium bearbeiten" : "Kriterium hinzufügen"}</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "auto",
          maxHeight: "60vh",
        }}
      >
        <TextField
          size="small"
          label="Name"
          fullWidth
          value={localCriteria.name}
          onChange={(e) => setLocalCriteria((prev) => ({ ...prev, name: e.target.value }))}
          required
        />

        <Box sx={{ width: "100%", display: "flex", flexDirection: "row", gap: 2 }}>
          <StyledButton
            sx={{ flex: 1 }}
            buttonType="add"
            customText="Text"
            size="small"
            onClick={() => handleOpenAddCriteriaNode(NodeDataType.TEXT)}
          />
          <StyledButton
            sx={{ flex: 1 }}
            buttonType="add"
            customText="Tabelle"
            size="small"
            onClick={() => handleOpenAddCriteriaNode(NodeDataType.TABLE)}
          />
          <StyledButton
            sx={{ flex: 1 }}
            buttonType="add"
            customText="Bild"
            size="small"
            onClick={() => handleOpenAddCriteriaNode(NodeDataType.IMAGE)}
          />
        </Box>

        {localCriteria.description.length > 0 && (
          <Box>
            {localCriteria.description.map((item) => (
              <Box
                key={item.id}
                sx={{
                  gap: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                {isNodeText(item) && <ReportNodeTextBox data={item} />}
                {isNodeTable(item) && <ReportNodeTableBox data={item} />}
                {isNodeImage(item) && <ReportNodeImageBox data={item} />}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <StyledButton
                    buttonType="edit"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleEditCriteriaNode(item)}
                  />
                  <StyledButton
                    buttonType="delete"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleDeleteCriteriaNode(item.id)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton
          buttonType="save"
          onClick={handleSave}
          disabled={!localCriteria.name?.trim()}
        />
      </DialogActions>

      {/* Criteria Node Edit Dialogs */}
      {criteriaNode && isNodeText(criteriaNode) && (
        <ReportNodeTextEditDialog
          open={criteriaNode.dataType === NodeDataType.TEXT}
          onClose={handleCloseAddCriteriaNode}
          node={criteriaNode}
          setNode={setCriteriaNode}
          onSave={handleAddCriteriaNode}
        />
      )}

      {criteriaNode && isNodeTable(criteriaNode) && (
        <ReportNodeTableEditDialog
          open={criteriaNode.dataType === NodeDataType.TABLE}
          onClose={handleCloseAddCriteriaNode}
          node={criteriaNode}
          setNode={setCriteriaNode}
          onSave={handleAddCriteriaNode}
        />
      )}

      {criteriaNode && isNodeImage(criteriaNode) && (
        <ReportNodeImageEditDialog
          open={criteriaNode.dataType === NodeDataType.IMAGE}
          onClose={handleCloseAddCriteriaNode}
          node={criteriaNode}
          setNode={setCriteriaNode}
          onSave={handleAddCriteriaNode}
        />
      )}
    </Dialog>
  )
}
